/**
 * kit.ts — общие элементы интерфейса кабинета (WEB-1874, добор порции E3).
 *
 * ЗАЧЕМ ОТДЕЛЬНЫЙ СЛОВАРЬ. `components/kit/` — не витрина, а БОЕВОЙ слой:
 * таблицы, подтверждения, пустые состояния, баннеры гейтов и модалка тарифов
 * рисуются во всех разделах кабинета. До этой правки каталог стоял под
 * постоянным исключением сторожа с причиной «витрина компонентов» — то есть
 * английский кабинет получал бы русские «Повторить», «Загрузка», «Отмена»,
 * «Скопировано» на каждом втором экране, и сторож молчал бы об этом по
 * построению. Исключение снято, надписи переехали сюда.
 *
 * Слова, которые уже есть в `common` (Отмена, Удалить, Загрузка), здесь НЕ
 * дублируются — потребитель берёт их из `common`.
 *
 * ⚠ Таблица тарифов (`DEFAULT_PLANS` в TierGateBanner) по смыслу принадлежит
 * разделу «деньги», но живёт в ките и рисуется его же модалкой. Держим её
 * здесь, рядом с потребителем; если модалка когда-нибудь переедет в
 * `components/billing/`, строки едут вместе с ней в словарь `billing`.
 */
import { defineDictionary } from '../define';

export const kitStrings = defineDictionary(
  {
    // ── Баннер причины, по которой публикация не пройдёт ─────────────────────
    gateReadonlyTitle: 'Locked: read-only',
    gateReadonlyBody:
      'This resource is over the limit of your current plan — saving and ' +
      'publishing are not allowed. Go back to the previous plan to edit again.',
    gateDebtTitle: 'Pay off the balance to publish',
    gateDebtBody:
      'Outstanding balance — saving and publishing are paused until it is paid.',
    gateNoPaymentTitle: 'Connect payments to publish',
    gateNoPaymentBody:
      'No payment system is connected — payment on the published page will not work.',
    gateNoPriceTitle: 'Set the prices to publish',
    gateNoPriceBody: 'A paid block has no price — the charge will not go through.',
    gatePriceConflictTitle: 'Resolve the price conflict to publish',
    gatePriceConflictBody:
      'The screen has several sources of price — it is unclear which one applies.',
    gateLegalTitle: 'Fill in the legal texts to publish',
    gateLegalBody:
      'The terms block has no links to the terms of use and the privacy policy.',
    gateDomainUnknownTitle: 'Could not check the domains',
    gateDomainUnknownBody:
      'We could not ask the server whether a domain is bound. Try running the check again.',
    gateNoDomainTitle: 'Bind a domain to publish',
    gateNoDomainBody:
      'Without a domain only the preview is available (visible to the project team).',
    gateEmptyContentTitle: 'Add at least one screen with content',
    gateEmptyContentBody:
      'An empty entity cannot be published — open the editor and fill it in.',
    gateTierTitle: 'Publishing is not available on the FREE plan',
    gateTierBody: 'Only the preview is available. Publishing funnels starts with Pro.',
    gateFix: 'Fix it',
    gateBindDomain: 'Bind a domain',
    gateOpenPreview: 'Open the preview',
    // WEB-2426: отказ сервера в публикации из таблиц раздела «Воронки».
    gateServerRefusedTitle: 'Publishing was refused',
    gateServerRefusedBody:
      'The server refused to publish. Fix the reason and try again.',

    // ── Апселл тарифа ───────────────────────────────────────────────────────
    tierGateTitle: 'Available on Pro',
    upgradePlan: 'Upgrade the plan',
    pricingModalTitle: 'Choose a plan',
    currentPlanTag: 'Your plan',
    currentPlanButton: 'Current plan',
    switchToPlan: (name: string) => `Switch to ${name}`,
    planPeriodMonth: '/mo',
    planFreeProjects: '1 project',
    // WEB-3840, остаток 25.10 (п. 94, 102): без оплаты — до 50 каждого вида.
    planFreeFunnel: 'Up to 50 quizzes, payment screens and funnels',
    planFreeRuns: '1,000 quiz runs/mo',
    planFreeCredits: '200 Cr once at start',
    planProProjects: '5 projects',
    // WEB-3840, остаток 25.10: на платном квизы и пейволлы без ограничения.
    planProFunnels: 'Unlimited quizzes and payment screens',
    planProDomains: 'Custom domains and publishing',
    planProRuns: '50,000 quiz runs/mo',
    planProCredits: '3,000 Cr/mo',

    // ── Состояние строки в таблицах и карточках ─────────────────────────────
    statusActive: 'Active',
    statusInactive: 'Unpublished (paused)',
    statusDraft: 'Draft',
    statusBroken: 'Broken',
    statusTrialing: 'Trial',
    statusPastDue: 'Past due',
    statusCanceled: 'Canceled',
    statusFrozen: 'Frozen',
    statusPending: 'Processing',
    statusExpired: 'Expired',
    statusSuspended: 'Suspended',

    // ── Подтверждение действия ──────────────────────────────────────────────
    confirmAction: 'Confirm',
    irreversibleWarning: 'This cannot be undone',
    typeNamePrefix: 'Type the name',
    typeNameSuffix: 'to continue:',
    passwordPlaceholder: 'Your password',

    // ── Поле «скопировать» ──────────────────────────────────────────────────
    copied: 'Copied',
    copyFailed: 'Could not copy',
    hideValue: 'Hide',
    showValue: 'Show',
    copyValue: 'Copy',

    // ── Таблица ─────────────────────────────────────────────────────────────
    tableLoadFailed: 'Could not load the data',
    tableSortHint: 'Click to sort',
    tableNoDataYet: 'No data yet',

    // ── Ошибка и пустое состояние ───────────────────────────────────────────
    retry: 'Retry',
    noOptions: 'No options',
    filterAll: 'All',

    // ── Публикация из списка ────────────────────────────────────────────────
    unpublish: 'Unpublish',
    publishAgain: 'Publish again',
    publish: 'Publish',

    // ── Массовый выбор строк ────────────────────────────────────────────────
    selectedCount: (count: number) => `Selected: ${count}`,
    clearSelection: 'Clear',
  },
  {
    gateReadonlyTitle: 'Ресурс заблокирован: только чтение',
    gateReadonlyBody:
      'Ресурс сверх лимита текущего тарифа — сохранять и публиковать нельзя. ' +
      'Вернитесь на прежний тариф, чтобы снова редактировать.',
    gateDebtTitle: 'Погасите долг, чтобы опубликовать',
    gateDebtBody:
      'Долг перед сервисом — сохранение и публикация приостановлены до его погашения.',
    gateNoPaymentTitle: 'Подключите приём платежей, чтобы опубликовать',
    gateNoPaymentBody:
      'Платёжная система не подключена — оплата на опубликованной странице не сработает.',
    gateNoPriceTitle: 'Укажите цены, чтобы опубликовать',
    gateNoPriceBody: 'У денежного блока не задана цена — списание не пройдёт.',
    gatePriceConflictTitle: 'Устраните конфликт цен, чтобы опубликовать',
    gatePriceConflictBody:
      'На экране несколько источников цены — какой применить, неясно.',
    gateLegalTitle: 'Заполните юридические тексты, чтобы опубликовать',
    gateLegalBody:
      'В блоке условий не заполнены ссылки на пользовательское соглашение и политику конфиденциальности.',
    gateDomainUnknownTitle: 'Не удалось проверить домены',
    gateDomainUnknownBody:
      'Не удалось спросить у сервера, привязан ли домен. Попробуйте повторить проверку.',
    gateNoDomainTitle: 'Привяжите домен, чтобы опубликовать',
    gateNoDomainBody:
      'Без домена доступен только предпросмотр (виден команде проекта).',
    gateEmptyContentTitle: 'Добавьте хотя бы один экран с контентом',
    gateEmptyContentBody:
      'Пустую сущность нельзя опубликовать — откройте редактор и наполните её.',
    gateTierTitle: 'Публикация недоступна на тарифе FREE',
    gateTierBody:
      'Доступен только предпросмотр. Публикация воронок открывается с Pro.',
    gateFix: 'Исправить',
    gateBindDomain: 'Привязать домен',
    gateOpenPreview: 'Открыть предпросмотр',
    // WEB-2426: отказ сервера в публикации из таблиц раздела «Воронки».
    gateServerRefusedTitle: 'Сервер не принял публикацию',
    gateServerRefusedBody:
      'Сервер отказал в публикации. Устраните причину и повторите.',

    tierGateTitle: 'Доступно на Pro',
    upgradePlan: 'Улучшить тариф',
    pricingModalTitle: 'Выбор тарифа',
    currentPlanTag: 'Ваш тариф',
    currentPlanButton: 'Текущий тариф',
    switchToPlan: (name: string) => `Перейти на ${name}`,
    planPeriodMonth: '/мес',
    planFreeProjects: '1 проект',
    planFreeFunnel: 'До 50 квизов, пейволлов и воронок',
    planFreeRuns: '1 000 запусков квиза/мес',
    planFreeCredits: '200 Cr разово при старте',
    planProProjects: '5 проектов',
    planProFunnels: 'Квизы и пейволлы без ограничения',
    planProDomains: 'Кастомные домены и публикация',
    planProRuns: '50 000 запусков квиза/мес',
    planProCredits: '3 000 Cr/мес',

    statusActive: 'Активен',
    statusInactive: 'Не опубликован (пауза)',
    statusDraft: 'Черновик',
    statusBroken: 'Сломана',
    statusTrialing: 'Пробный период',
    statusPastDue: 'Просрочена',
    statusCanceled: 'Отменена',
    statusFrozen: 'Заморожен',
    statusPending: 'В обработке',
    statusExpired: 'Истёк',
    statusSuspended: 'Приостановлен',

    confirmAction: 'Подтвердить',
    irreversibleWarning: 'Действие необратимо',
    typeNamePrefix: 'Введите название',
    typeNameSuffix: 'чтобы продолжить:',
    passwordPlaceholder: 'Ваш пароль',

    copied: 'Скопировано',
    copyFailed: 'Не удалось скопировать',
    hideValue: 'Скрыть',
    showValue: 'Показать',
    copyValue: 'Копировать',

    tableLoadFailed: 'Не удалось загрузить данные',
    tableSortHint: 'Нажмите, чтобы отсортировать',
    tableNoDataYet: 'Пока нет данных',

    retry: 'Повторить',
    noOptions: 'Нет вариантов',
    filterAll: 'Все',

    unpublish: 'Снять с публикации',
    publishAgain: 'Опубликовать снова',
    publish: 'Опубликовать',

    selectedCount: (count: number) => `Выбрано ${count}`,
    clearSelection: 'Сбросить',
  },
);
